import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { format } from 'date-fns';

export interface PayslipData {
  employee: {
    name: string;
    id?: string;
    email?: string;
    [key: string]: any;
  };
  business: {
    name: string;
    address?: string;
    phone?: string;
    email?: string;
    [key: string]: any;
  };
  period: {
    start: Date;
    end: Date;
    month: number;
    year: number;
  };
  payment: {
    hoursWorked: number;
    grossPay: number;
    netPay: number;
    calculationMethod: string;
    autoCalculated: boolean;
  };
  location: {
    countryCode?: string;
    currency?: string;
  };
  [key: string]: any;
}

export interface PayslipPDFOptions {
  password?: string;
  includeEmployerCosts?: boolean;
  watermark?: string;
  language?: string;
}

/**
 * Universal payslip PDF generator that works across all countries
 */
export class PayslipPDFGenerator {
  private pdf: jsPDF;
  private pageWidth: number = 210; // A4 width in mm
  private pageHeight: number = 297; // A4 height in mm
  private margin: number = 20;
  private contentWidth: number;
  private lineHeight: number = 6;
  private yPos: number;

  constructor() {
    this.pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    this.contentWidth = this.pageWidth - (2 * this.margin);
    this.yPos = this.margin;
  }

  /**
   * Generate a complete payslip PDF
   */
  async generatePayslip(data: PayslipData, options: PayslipPDFOptions = {}): Promise<jsPDF> {
    try {
      // Add password protection if provided
      if (options.password) {
        try {
          this.pdf.setProtection(128, options.password, options.password, {
            print: 1,
            modify: 0,
            copy: 1,
            'annot-forms': 0
          });
        } catch (error) {
          console.warn('Password protection not supported in this jsPDF version:', error);
        }
      }

      // Set up fonts
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setFontSize(10);

      // Build PDF sections
      this.addHeader(data);
      this.addEmployeeInfo(data);
      this.addPaymentDetails(data);
      this.addFooter(data);

      // Add watermark if specified
      if (options.watermark) {
        this.addWatermark(options.watermark);
      }

      return this.pdf;
    } catch (error) {
      console.error('Error generating payslip PDF:', error);
      throw error;
    }
  }

  /**
   * Generate and download payslip PDF
   */
  async generateAndDownload(data: PayslipData, options: PayslipPDFOptions = {}): Promise<void> {
    const pdf = await this.generatePayslip(data, options);
    const filename = this.generateFilename(data);
    pdf.save(filename);
  }

  /**
   * Generate PDF from HTML element (fallback method)
   */
  async generateFromHTML(element: HTMLElement, filename: string): Promise<void> {
    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF from HTML:', error);
      throw error;
    }
  }

  private addHeader(data: PayslipData): void {
    const centerX = this.pageWidth / 2;

    try {
      // Company header
      this.pdf.setFontSize(16);
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.text(data.business.name, centerX, this.yPos, { align: 'center' });
      this.yPos += 8;

      this.pdf.setFontSize(10);
      this.pdf.setFont('helvetica', 'normal');
      if (data.business.address) {
        this.pdf.text(data.business.address, centerX, this.yPos, { align: 'center' });
        this.yPos += 5;
      }
      if (data.business.phone || data.business.email) {
        const contact = [data.business.phone, data.business.email].filter(Boolean).join(' | ');
        this.pdf.text(contact, centerX, this.yPos, { align: 'center' });
        this.yPos += 5;
      }

      // Title
      this.yPos += 10;
      this.pdf.setFontSize(18);
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.text('PAYSLIP', centerX, this.yPos, { align: 'center' });
      this.yPos += 8;

      this.pdf.setFontSize(12);
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.text(
        `Period: ${this.formatPayrollPeriod(data.period.start, data.period.end)}`,
        centerX, this.yPos, { align: 'center' }
      );
      this.yPos += 15;

      // Draw header separator
      this.pdf.setLineWidth(0.5);
      this.pdf.line(this.margin, this.yPos, this.pageWidth - this.margin, this.yPos);
      this.yPos += 10;

    } catch (error) {
      console.error('Error adding PDF header:', error);
      // Add basic header as fallback
      this.pdf.text('PAYSLIP', centerX, this.yPos, { align: 'center' });
      this.yPos += 20;
    }
  }

  private addEmployeeInfo(data: PayslipData): void {
    try {
      this.pdf.setFontSize(11);
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.text('EMPLOYEE INFORMATION', this.margin, this.yPos);
      this.yPos += this.lineHeight + 2;

      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setFontSize(9);

      // Employee info
      const employeeInfo = [
        `Name: ${data.employee.name}`,
        `Employee ID: ${data.employee.id || 'N/A'}`,
        `Email: ${data.employee.email || 'N/A'}`,
        `Country: ${data.location.countryCode || 'N/A'}`
      ];

      employeeInfo.forEach((info) => {
        this.pdf.text(info, this.margin, this.yPos);
        this.yPos += this.lineHeight;
      });

      this.yPos += 10;

    } catch (error) {
      console.error('Error adding employee info:', error);
      this.pdf.text(`Employee: ${data.employee.name}`, this.margin, this.yPos);
      this.yPos += 20;
    }
  }

  private addPaymentDetails(data: PayslipData): void {
    try {
      this.pdf.setFontSize(11);
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.text('PAYMENT DETAILS', this.margin, this.yPos);
      this.yPos += this.lineHeight + 2;

      // Table headers
      this.pdf.setFontSize(9);
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.text('Description', this.margin, this.yPos);
      this.pdf.text('Amount', this.margin + this.contentWidth - 40, this.yPos, { align: 'right' });
      this.yPos += this.lineHeight;

      // Draw line under headers
      this.pdf.setLineWidth(0.3);
      this.pdf.line(this.margin, this.yPos - 1, this.margin + this.contentWidth, this.yPos - 1);
      this.yPos += 2;

      this.pdf.setFont('helvetica', 'normal');

      // Payment items
      const paymentItems = [
        { label: 'Hours Worked', amount: `${data.payment.hoursWorked.toFixed(1)}h` },
        { label: 'Gross Pay', amount: this.formatCurrency(data.payment.grossPay, data.location) },
        { label: 'Net Pay', amount: this.formatCurrency(data.payment.netPay, data.location), isFinal: true }
      ];

      paymentItems.forEach(item => {
        if (item.isFinal) {
          this.pdf.setFont('helvetica', 'bold');
        }
        this.pdf.text(item.label, this.margin, this.yPos);
        this.pdf.text(item.amount, this.margin + this.contentWidth - 40, this.yPos, { align: 'right' });
        if (item.isFinal) {
          this.pdf.setFont('helvetica', 'normal');
        }
        this.yPos += this.lineHeight;
      });

      // Total line
      this.pdf.setLineWidth(0.5);
      this.pdf.line(this.margin, this.yPos, this.margin + this.contentWidth, this.yPos);
      this.yPos += this.lineHeight + 8;

    } catch (error) {
      console.error('Error adding payment details:', error);
      this.pdf.text(`Gross Pay: ${this.formatCurrency(data.payment.grossPay, data.location)}`, this.margin, this.yPos);
      this.yPos += 10;
      this.pdf.text(`Net Pay: ${this.formatCurrency(data.payment.netPay, data.location)}`, this.margin, this.yPos);
      this.yPos += 20;
    }
  }

  private addFooter(data: PayslipData): void {
    try {
      const footerY = this.pageHeight - 30;

      this.pdf.setFontSize(8);
      this.pdf.setFont('helvetica', 'normal');

      const footerInfo = [
        `Generated: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`,
        `Calculation: ${data.payment.calculationMethod}`,
        `${data.payment.autoCalculated ? 'Automatically calculated' : 'Manually calculated'}`
      ];

      footerInfo.forEach((info, index) => {
        this.pdf.text(info, this.margin + (index * (this.contentWidth / 3)), footerY);
      });

      // Confidentiality notice
      this.pdf.setFontSize(7);
      this.pdf.text(
        'This document contains confidential payroll information. Handle securely.',
        this.margin + this.contentWidth / 2,
        footerY + 10,
        { align: 'center' }
      );

    } catch (error) {
      console.error('Error adding PDF footer:', error);
      this.pdf.text('Confidential Payroll Document', this.margin, this.pageHeight - 20);
    }
  }

  private addWatermark(text: string): void {
    this.pdf.setTextColor(200, 200, 200);
    this.pdf.setFontSize(48);
    this.pdf.setFont('helvetica', 'bold');

    const centerX = this.pageWidth / 2;
    const centerY = this.pageHeight / 2;

    this.pdf.text(text, centerX, centerY, {
      align: 'center',
      angle: 45
    });

    // Reset text color
    this.pdf.setTextColor(0, 0, 0);
  }

  private formatCurrency(amount: number, location: { countryCode?: string; currency?: string }): string {
    if (location.currency) {
      return `${location.currency}${amount.toFixed(2)}`;
    }

    // Default currency based on country
    if (location.countryCode && this.isEuroCountry(location.countryCode)) {
      return `€${amount.toFixed(2)}`;
    }

    return `$${amount.toFixed(2)}`;
  }

  private isEuroCountry(countryCode: string): boolean {
    const euroCountries = ['SK', 'DE', 'AT', 'FR', 'ES', 'IT', 'NL', 'BE', 'LU', 'IE', 'PT', 'FI', 'GR', 'SI', 'CY', 'MT', 'EE', 'LV', 'LT'];
    return euroCountries.includes(countryCode);
  }

  private formatPayrollPeriod(start: Date, end: Date): string {
    return `${format(start, 'dd/MM/yyyy')} - ${format(end, 'dd/MM/yyyy')}`;
  }

  private generateFilename(data: PayslipData): string {
    const employeeName = data.employee.name.replace(/\s+/g, '_');
    const period = `${data.period.year}-${data.period.month.toString().padStart(2, '0')}`;
    return `${employeeName}_payslip_${period}.pdf`;
  }
}

/**
 * Convenience function to generate a simple payslip PDF
 */
export async function generateSimplePayslip(data: PayslipData, options: PayslipPDFOptions = {}): Promise<void> {
  const generator = new PayslipPDFGenerator();
  await generator.generateAndDownload(data, options);
}

/**
 * Convenience function to generate payslip PDF and return the jsPDF instance
 */
export async function createPayslipPDF(data: PayslipData, options: PayslipPDFOptions = {}): Promise<jsPDF> {
  const generator = new PayslipPDFGenerator();
  return await generator.generatePayslip(data, options);
}